/**
 * Карточка пользователя (§51).
 *
 * Ручные операции с единицами требуют причины: она попадает в историю баланса
 * и видна самому пользователю. Ключ идемпотентности выдаётся форме один раз —
 * двойной клик не должен начислять дважды.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { adminApi } from '../../api/admin';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { formatDateTime } from '../../i18n/format';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../i18n/errors';

export function AdminUserPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [units, setUnits] = useState('');
  const [reason, setReason] = useState('');
  // Ключ живёт вместе с формой: пока её не очистили, повтор запроса
  // считается тем же самым начислением.
  const [formKey, setFormKey] = useState(() => crypto.randomUUID());

  const user = useQuery({ queryKey: ['admin', 'user', id], queryFn: () => adminApi.user(id) });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin', 'user', id] });

  const adjust = useMutation({
    mutationFn: () =>
      adminApi.adjustUnits(id, {
        units: Number(units),
        reason,
        idempotencyKey: formKey,
      }),
    onSuccess: async () => {
      setUnits('');
      setReason('');
      setFormKey(crypto.randomUUID());
      await refresh();
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: 'active' | 'blocked') => adminApi.setStatus(id, status),
    onSuccess: refresh,
  });

  const setService = useMutation({
    mutationFn: (isService: boolean) => adminApi.setService(id, isService),
    onSuccess: refresh,
  });

  const data = user.data;

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={() => navigate('/admin/users')}
        className="flex w-fit items-center gap-1.5 text-meta text-content-muted transition-colors duration-fast hover:text-content"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
        {t('admin.backToList')}
      </button>

      {data ? (
        <>
          <section className="grid grid-cols-2 gap-3 rounded-card border border-line bg-surface p-4 text-meta lg:grid-cols-4">
            <Field label={t('auth.field.email')} value={data.email} />
            <Field label={t('auth.field.name')} value={data.name ?? '—'} />
            <Field
              label={t('admin.user.role')}
              value={data.isService ? `${data.role} · ${t('admin.user.serviceShort')}` : data.role}
            />
            <Field
              label={t('admin.job.status')}
              value={data.status}
              tone={data.status === 'blocked' ? 'bad' : undefined}
            />
            <Field label={t('admin.user.available')} value={String(data.available)} />
            <Field label={t('admin.user.reserved')} value={String(data.reserved)} />
            <Field label={t('admin.user.spentTotal')} value={String(data.spentUnitsTotal)} />
            <Field label={t('admin.user.projects')} value={String(data.projectCount)} />
            <Field label={t('admin.user.payments')} value={String(data.paymentCount)} />
            <Field label={t('admin.user.ownCode')} value={data.referralCode ?? '—'} />
            <Field label={t('admin.user.viaCode')} value={data.referredByCode ?? '—'} />
            <Field
              label={t('nav.personaShort')}
              value={
                data.persona
                  ? [
                      data.persona.status,
                      ...(data.persona.hasAvatar ? [t('persona.part.avatar')] : []),
                      ...(data.persona.hasVoice ? [t('persona.part.voice')] : []),
                    ].join(', ')
                  : '—'
              }
            />
          </section>

          <section className="flex flex-col gap-2 rounded-card border border-line bg-surface p-4">
            <p className="text-meta text-content-secondary">{t('admin.user.adjustTitle')}</p>
            <div className="flex flex-wrap gap-2">
              <input
                value={units}
                onChange={(event) => setUnits(event.target.value)}
                type="number"
                placeholder={t('admin.user.unitsPlaceholder')}
                className="h-9 w-48 rounded-control border border-line bg-surface-secondary px-3 text-meta text-content placeholder:text-content-muted focus:border-accent focus:outline-none"
              />
              <input
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={t('admin.user.reasonPlaceholder')}
                className="h-9 flex-1 rounded-control border border-line bg-surface-secondary px-3 text-meta text-content placeholder:text-content-muted focus:border-accent focus:outline-none"
              />
              <Button
                variant="secondary"
                disabled={adjust.isPending || !units || reason.trim().length < 3}
                onClick={() => adjust.mutate()}
              >
                {adjust.isPending ? t('admin.user.applying') : t('admin.user.apply')}
              </Button>
            </div>
            {adjust.isError ? <Alert tone="error">{errorText(adjust.error)}</Alert> : null}
          </section>

          <section className="flex items-center gap-3 rounded-card border border-line bg-surface p-4">
            <p className="flex-1 text-meta text-content-secondary">
              {data.status === 'blocked' ? t('admin.user.blockedNote') : t('admin.user.blockNote')}
            </p>
            <Button
              variant="tertiary"
              disabled={setStatus.isPending}
              onClick={() => setStatus.mutate(data.status === 'blocked' ? 'active' : 'blocked')}
            >
              {data.status === 'blocked' ? t('admin.user.unblock') : t('admin.user.block')}
            </Button>
          </section>

          {/*
            §41: имена внешних сервисов не показываются пользователям — это
            критическое требование. Служебная отметка делает исключение
            поимённо, для тех, кто продукт тестирует, а не пользуется им.
          */}
          <section className="flex flex-col gap-2 rounded-card border border-line bg-surface p-4">
            <p className="text-meta text-content-secondary">{t('admin.user.serviceTitle')}</p>
            <p className="text-meta text-content-muted">
              {data.isService ? t('admin.user.serviceOn') : t('admin.user.serviceOff')}
            </p>
            <Button
              variant="tertiary"
              disabled={setService.isPending}
              onClick={() => setService.mutate(!data.isService)}
            >
              {data.isService ? t('admin.user.serviceRemove') : t('admin.user.serviceAdd')}
            </Button>
          </section>

          <section className="rounded-card border border-line bg-surface p-4">
            <p className="mb-2 text-meta text-content-secondary">{t('settings.consents')}</p>
            <div className="flex flex-col gap-1 text-meta">
              {data.consents.map((consent) => (
                <div key={`${consent.type}-${consent.acceptedAt}`} className="flex gap-3">
                  <span className="w-48 text-content">{consent.type}</span>
                  <span className="text-content-secondary">v{consent.version}</span>
                  <span className="text-content-muted">{formatDateTime(consent.acceptedAt)}</span>
                </div>
              ))}
            </div>
          </section>
        </>
      ) : (
        <p className="text-meta text-content-muted">{t('common.loading')}</p>
      )}
    </div>
  );
}

function Field({ label, value, tone }: { label: string; value: string; tone?: 'bad' }) {
  return (
    <div>
      <p className="text-content-muted">{label}</p>
      <p className={tone === 'bad' ? 'text-error' : 'text-content'}>{value}</p>
    </div>
  );
}
